<template>
    <div class="clustors-main">
        <div class="clustors-header">
            <input type="text" class="form-control clustors-search" placeholder="Search clusters..."
                v-model="search_qty" @input="filteredMembers()" />
            <button class="btn btn-primary clustors-add-btn" @click="adminAdd" v-if="editable">
                {{ $t('add_cluster') }}
            </button>
        </div>
        <div v-if="!loading_list && clustersByZone.length" class="clustors-list">
          <div v-for="zone in clustersByZone" :key="zone.name" class="zone-group">
            <div class="zone-heading">
              <span>{{ zone.name }}</span>
            </div>
            <div v-for="panchayath in zone.panchayaths" :key="panchayath.name" class="panchayath-group">
              <div class="panchayath-heading">
                <span>{{ panchayath.name }} <small class="text-primary" v-if="panchayath.zone_name">({{ panchayath.zone_name }})</small> </span>
                <div class="panchayath-actions">
                  <button class="btn btn-outline-secondary btn-sm" @click="panchayathView(panchayath)">&#9998;</button>
                  <button v-if="!(deleteMode && deleteModeGroup === panchayath.name)" 
                          class="btn btn-outline-danger btn-sm" 
                          @click="startDeleteMode(panchayath)">&#10006;</button>
                  <div v-if="deleteMode && deleteModeGroup === panchayath.name" class="delete-mode-actions">
                    <button class="btn btn-outline-danger btn-sm" @click="deleteSelectedClusters">{{ $t('delete_selected') }}</button>
                    <button class="btn btn-outline-secondary btn-sm" @click="cancelDeleteMode">{{ $t('cancel') }}</button>
                  </div>
                </div>
              </div>
              <div v-for="member_list in panchayath.clusters" :key="member_list.id" class="clustors-card">
                <div class="cluster-content">
                  <input v-if="deleteMode && deleteModeGroup === panchayath.name" 
                         type="checkbox" 
                         :value="member_list.id" 
                         v-model="selectedForDelete" 
                         class="cluster-checkbox" />
                  <span class="clustors-name">{{ member_list.ward ? member_list.ward.name : '' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!loading_list && panchayaths.length && !clustersByZone.length" class="text-center text-muted py-5">
            <i class="fa fa-users fa-2x mb-3"></i>
            <div>{{ $t('no_clusters') }}</div>
        </div>
        <div v-if="loading_list" class="text-center py-5">
            <div class="spinner-border text-primary"></div>
        </div>

        <!-- Modal -->
        <div class="modal fade action-sheet" id="addMemberModel" tabindex="-1" role="dialog">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ $t('add_cluster') }}</h5>
                    </div>
                    <div class="modal-body">
                        <div class="action-sheet-content">
                            <Form @submit="addMember" :validation-schema="schema">
                                <div class="form-group basic">
                                    <label for="zone">{{ $t('zone') }}</label>
                                    <select v-model="selectedZone" @change="onZoneChange" class="form-control" id="zone" required>
                                        <option value="" selected>{{ $t('select_zone') }}</option>
                                        <option v-for="z in zones" :key="z.id" :value="z.id">{{ z.name }}</option>
                                    </select>
                                </div>
                                <div class="form-group basic">
                                    <label for="panchayath">{{ $t('panchayath') }}</label>
                                    <select v-model="selectedPanchayath" @change="onPanchayathChange"
                                        class="form-control" id="panchayath" required>
                                        <option value="" selected>{{ $t('select_panchayath') }}</option>
                                        <option v-for="p in panchayaths" :key="p.id" :value="p.id">{{ p.name }}</option>
                                    </select>
                                </div>
                                <div class="form-group basic">
                                    <label for="ward">{{ $t('cluster') }}</label>
                                    
                                    <!-- Select All Option -->
                                    <div class="select-all-container" v-if="wards.length > 1">
                                        <label class="select-all-label">
                                            <input 
                                                type="checkbox" 
                                                class="select-all-checkbox"
                                                :checked="allWardsSelected"
                                                @change="toggleSelectAll"
                                                :disabled="loading"
                                            />
                                            <span class="select-all-text">{{ $t('select_all_clusters') }}</span>
                                        </label>
                                    </div>
                                    
                                    <!-- Clusters Grid -->
                                    <div class="clusters-grid" v-if="wards.length">
                                        <div 
                                            v-for="ward in wards" 
                                            :key="ward.id"
                                            class="cluster-item"
                                            :class="{ selected: isWardSelected(ward) }"
                                            @click="toggleWardSelection(ward)"
                                        >
                                            <input 
                                                type="checkbox" 
                                                class="cluster-checkbox"
                                                :checked="isWardSelected(ward)"
                                                @change="toggleWardSelection(ward)"
                                                :disabled="loading"
                                                @click.stop
                                            />
                                            <span class="cluster-name">{{ $i18n.locale === 'ml' && ward.name_mal ? ward.name_mal : ward.name }}</span>
                                        </div>
                                    </div>
                                    
                                    <!-- Error Message -->
                                    <div class="text-danger small mt-1" v-if="wardSelectionError">
                                        {{ wardSelectionError }}
                                    </div>
                                    
                                    <!-- Selected Count -->
                                    <div class="selected-count" v-if="selectedWards.length">
                                        <small class="text-muted">
                                            {{ selectedWards.length }} {{ $t('of') }} {{ wards.length }} {{ $t('cluster_selected') }}
                                        </small>
                                    </div>
                                    
                                    <!-- No clusters message -->
                                    <div v-if="selectedPanchayath && !wards.length" class="text-muted small mt-2">
                                        {{ $t('no_clusters_available') }}
                                    </div>

                                    <ErrorMessage name="ward" class="error-message" />
                                </div>

                                <div class="form-group basic mb-0 text-right d-flex justify-content-end gap-2">
                                    <button type="submit" class="btn btn-primary btn-block btn-lg" :disabled="loading">
                                        <span v-show="loading" class="spinner-border spinner-border-sm"></span>
                                        {{ $t('save') }}
                                    </button>
                                </div>
                            </Form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <alert-model :type="alert_type" :message="alert_message" ref="alert_model"></alert-model>
        <dialog-model :message="dialog_message" :action_method="dialog_action"
            :action_method_params="dialog_action_params" ref="dialog_model"></dialog-model>
    </div>
</template>

<script>
import { Form, ErrorMessage } from "vee-validate";
import * as yup from "yup";
import UserService from "../../services/user.service";
import $ from 'jquery';

import AlertModel from "../layouts/AlertModel.vue";
import DialogModel from "../layouts/DialogModel.vue";

export default {
    name: 'Clustors List',
    props: {
        unit_id: null,
        district_id: null
    },
    components: {
        Form,
        // Field,
        ErrorMessage,
        AlertModel,
        DialogModel,
    },
    data() {
        const schema = yup.object().shape({
            // selectedWard: yup.string().required("ക്ലസ്റ്റർ തിരഞ്ഞെടുക്കുക!")
        });
        return {
            loading: false,
            loading_list: false,
            message: "",
            country_code: '91',
            country_code_whatsapp: '91',
            mobile_number: '',
            whatsapp_number: '',
            wing: '',
            schema,
            stay: '',
            user_level: "",
            sub_levels: [],
            member_lists: [],
            lists: [],
            orgnal_member_lists: [],
            trash_list: [],
            unit: [],
            editable_id: '',
            editable: true,
            add_admin: false,
            abroad_fields: false,
            pin_number: '',
            deletable_id: '',
            reason_to_delete: '',
            message_delete: '',
            delete_others: false,
            delete_other_text: '',
            member_id: '',
            search_qty: '',
            verification_status_list: [],
            verification_status: '',
            not_verification_status: '',
            not_verification_status_: '',
            verification_remark: '',
            verification_member_id: '',
            verification_message: '',
            verification_report: false,
            isSubscriber: '',
            receipt_no: '',
            subscription_no: '',
            nerpatham: [],
            alert_type: '',
            alert_message: '',
            dialog_message: '',
            dialog_action: '',
            dialog_action_params: [],
            verification_check: [],
            not_verification_check: [],
            country_list: [],
            isAbroad: '',
            abroadPlace: '',
            abroadCountry: '',
            lajna_member: '',
            lajna_member_id: '',
            permissions: {
                view_event_registrations: '',
                qhls: [],
                nerpatham: [],
                executives: [],
                members: [],
                verification: []
            },
            districts: [],
            panchayaths: [],
            wards: [],
            zones: [],
            selectedDistrict: null,
            selectedPanchayath: null,
            selectedZone: null,
            selectedWard: null,
            selectedWards: [],
            editing_panchayath: false,
            deleteMode: false,
            deleteModeGroup: null,
            selectedForDelete: [],
        };
    },

    computed: {
        currentUser() {
            return this.$store.state.auth.user;
        },
        clustersByZone() {
            // First group by zone
            const groups = {};

            // Create a set of panchayath IDs that belong to the current district for efficient lookup.
            const panchayathIdsInCurrentDistrict = new Set(this.panchayaths.map(p => p.id));

            // Filter the lists to only include clusters whose panchayath is in the current district.
            const filteredLists = this.lists.filter(item =>
                item.ward && panchayathIdsInCurrentDistrict.has(item.ward.panchayath_id)
            );

            filteredLists.forEach(item => {
                const zoneId = item.zone?.id;
                if (!zoneId) return;
                
                if (!groups[zoneId]) {
                    groups[zoneId] = {
                        name: item.zone.name,
                        panchayaths: {}
                    };
                }
                
                // Then group by panchayath within each zone
                const panchayathId = item.ward?.panchayath_id;
                if (!panchayathId) return;
                
                if (!groups[zoneId].panchayaths[panchayathId]) {
                    groups[zoneId].panchayaths[panchayathId] = {
                        name: this.getPanchayathName(panchayathId),
                        clusters: []
                    };
                }
                
                groups[zoneId].panchayaths[panchayathId].clusters.push(item);
            });

            // Convert to array format for v-for
            return Object.values(groups).map(zone => ({
                ...zone,
                panchayaths: Object.values(zone.panchayaths)
            }));
        },
        allWardsSelected() {
            return this.wards.length > 0 && this.selectedWards.length === this.wards.length;
        },
    },
    async mounted() {
        console.log(this.permissions.verification);
        if (!this.currentUser) {
            location.replace('/');
        } else {
            this.user_level = 2;

            // Wait for panchayaths to load first to prevent flash of "No data"
            await this.loadUserDistrictAndPanchayaths();
            
            // Use district_id if user_level == 2, else unit_id
            if (this.user_level === 2) {
                this.getMembersList(1, this.district_id || this.$route.params.id || this.currentUser.district_id);
            } else {
                this.getMembersList(1, this.unit_id ? this.unit_id : this.currentUser.unit_id);
            }
            if ((this.unit_id == this.currentUser.unit_id) || this.currentUser.user_level <= 4) {
                this.editable = true;
            } else {
                this.editable = false;
            }
            this.$root.updateParent('District Clusters');
        }
        window.onpopstate = function () {
            if ($('body').hasClass('modal-open')) {
                location.reload();
            }
        };
    },
    methods: {
        wardLabel(option) {
            return option && option.name ? option.name : '';
        },
        zoneView(zone) {
            $('#addMemberModel').modal('show');
            this.selectedZone = zone.id;
            this.onZoneChange().then(() => {
                this.selectedPanchayath = null;
                this.selectedWards = [];
            });
            this.editable_id = null;
            this.editing_panchayath = false;
        },
        getPanchayathName(panchayath_id) {
            const p = this.panchayaths.find(x => x.id === panchayath_id);
            return p ? p.name : '';
        },
        getMembersList(wing = null) {
            this.loading_list = true;
            let data = {
                unit_id: this.user_level === 2 ? undefined : (this.unit_id ? this.unit_id : this.currentUser.unit_id),
                district_id: this.$route.params.id,
                wing: wing,
                members_list: false,
                temp_members_list: true
            };
            UserService.authPostRequest('get-district-clusters', data).then(
                (response) => {
                    if (response.data.status == 'success') {
                        this.member_lists = response.data.data.list;
                        this.search_qty = '';
                        this.orgnal_member_lists = response.data.data.list;
                        this.trash_list = response.data.data.trash_list;
                        this.lists = response.data.data.list;
                        this.unit = response.data.data.unit;
                        this.wing = wing;
                        this.permissions = response.data.data.permissions;
                        this.loading_list = false;
                    } else {
                        this.loading_list = false; // Also set to false on non-success responses
                    }
                },
                (error) => {
                    this.loading = false;
                    this.loading_list = false; // Also set to false on error
                    this.message =
                        (error.response &&
                            error.response.data &&
                            error.response.data.message) ||
                        error.message ||
                        error.toString();
                }
            );
        },
        memberView(admin) {
            $('#addMemberModel').modal('show');
            this.selectedPanchayath = admin.ward ? admin.ward.panchayath_id : '';
            this.onPanchayathChange().then(() => {
                // If editing, preselect all wards (clusters) assigned to this Panchayat
                // Suppose admin.ward_ids is an array of selected ward IDs
                if (Array.isArray(admin.ward_ids)) {
                    this.selectedWards = this.wards.filter(w => admin.ward_ids.includes(w.id));
                } else if (admin.ward_id) {
                    this.selectedWards = [this.wards.find(w => w.id === admin.ward_id)];
                } else {
                    this.selectedWards = [];
                }
            });
            this.editable_id = admin.id;
        },
        adminDeleteChange(event) {
            if (event.target.value == 'Other') {
                this.delete_others = true;
            } else {
                this.delete_others = false;
            }
        },
        adminDelete(member) {
            var data = {
                id: member.id,
            }
            this.$swal.fire({
                title: '',
                text: "ഉറപ്പായും ഡിലീറ്റ് ചെയ്യണോ ?",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then((result) => {
                if (result.isConfirmed) {
                    UserService.authPostRequest('delete-district-clusters', data).then(
                        (response) => {
                            if (response.data.status == 'success') {
                                this.deletable_id = ''
                                this.reason_to_delete = ''
                                this.delete_others = false
                                this.delete_other_text = ''
                                $('#deleteMemberModel').modal('hide');
                                this.$swal.fire(
                                    '',
                                    response.data.message,
                                    'success'
                                );
                                this.getMembersList(1);
                                this.loading = false;
                            } else if (response.data.status == 'warning') {
                                $('#deleteMemberModel').modal('hide');
                                this.$swal.fire(
                                    '',
                                    response.data.message,
                                    'warning'
                                );
                                this.getMembersList(1);
                                this.loading = false;
                            } else {
                                this.loading = true;
                            }
                        },
                        (error) => {
                            this.loading = false;
                            this.message =
                                (error.response &&
                                    error.response.data &&
                                    error.response.data.message) ||
                                error.message ||
                                error.toString();
                        }
                    );
                }
            });


        },
        memberDelete() {
            if (this.reason_to_delete) {
                this.message_delete = '';
                var data = {
                    id: this.deletable_id,
                    reason: !this.delete_others ? this.reason_to_delete : this.delete_other_text
                }
                UserService.authPostRequest('delete-members', data).then(
                    (response) => {
                        if (response.data.status == 'success') {
                            this.deletable_id = ''
                            this.reason_to_delete = ''
                            this.delete_others = false
                            this.delete_other_text = ''
                            $('#deleteMemberModel').modal('hide');
                            this.$swal.fire(
                                '',
                                response.data.message,
                                'success'
                            );
                            this.getMembersList(1);
                            this.loading = false;
                        } else if (response.data.status == 'warning') {
                            $('#deleteMemberModel').modal('hide');
                            this.$swal.fire(
                                '',
                                response.data.message,
                                'warning'
                            );
                            this.getMembersList(1);
                            this.loading = false;
                        } else {
                            this.loading = true;
                        }
                    },
                    (error) => {
                        this.loading = false;
                        this.message =
                            (error.response &&
                                error.response.data &&
                                error.response.data.message) ||
                            error.message ||
                            error.toString();
                    }
                );
            } else {
                this.message_delete = 'ഡിലീറ്റ് ചെയ്യാനുള്ള കാരണം സെലക്ട് ചെയ്യുക';
            }

        },
        showActionModel() {
            this.pin_number = '';
        },
        getPin(member) {
            this.loading = true;
            this.pin_number = '';
            var user = {
                id: member.id
            }
            UserService.authPostRequest('get-pin', user).then(
                (response) => {
                    if (response.data.status == 'success') {
                        this.pin_number = response.data.data.pin;
                        this.loading = false;
                    } else if (response.data.status == 'error') {
                        this.loading = false;
                    } else {
                        this.loading = false;
                    }
                },
                (error) => {
                    if (error)
                        this.loading = false;
                }
            );
        },
        adminAdd() {
            $('#addMemberModel').modal('show');
            this.wing = '';
            this.mobile_number = '';
            this.name = '';
            this.country_code = '91';
            this.editable_id = null;
            this.add_admin = true;
            this.selectedPanchayath = null;
            this.selectedWards = [];
            this.wards = [];
            this.editing_panchayath = false;
        },
        addMember(user) {
            user.id = this.editable_id;
            user.district_id = this.$route.params.id,
            user.panchayath_id = this.selectedPanchayath;
            user.zone_id = this.selectedZone;
            user.added_by = this.currentUser.id;
            this.loading = true;
            
            if (this.editing_panchayath) {
                // If editing a Panchayat, update all existing clusters
                const selectedWards = Array.isArray(this.selectedWards) ? this.selectedWards : [this.selectedWards];
                const requests = selectedWards.map(wardObj => {
                    const userData = { ...user, ward_id: wardObj.id };
                    return UserService.authPostRequest('store-district-clusters', userData);
                });
                Promise.all(requests).then(responses => {
                    let hasSuccess = false;
                    responses.forEach(response => {
                        if (response.data.status == 'success') {
                            hasSuccess = true;
                        }
                    });
                    $('#addMemberModel').modal('hide');
                    if (hasSuccess) {
                        this.$swal('', 'Panchayat clusters updated successfully', 'success');
                    } else {
                        this.$swal('', 'No clusters were updated', 'warning');
                    }
                    this.getMembersList(user.wing);
                    this.loading = false;
                    this.editing_panchayath = false;
                }).catch(error => {
                    this.loading = false;
                    this.message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
                });
            } else {
                const selectedWards = Array.isArray(this.selectedWards) ? this.selectedWards : [this.selectedWards];
                const requests = selectedWards.map(wardObj => {
                    const userData = { ...user, ward_id: wardObj.id };
                    return UserService.authPostRequest('store-district-clusters', userData);
                });
                Promise.all(requests).then(responses => {
                    let hasSuccess = false;
                    responses.forEach(response => {
                        if (response.data.status == 'success') {
                            hasSuccess = true;
                        }
                    });
                    $('#addMemberModel').modal('hide');
                    if (hasSuccess) {
                        this.$swal('', 'Clusters added successfully', 'success');
                    } else {
                        this.$swal('', 'No clusters were added', 'warning');
                    }
                    this.getMembersList(user.wing);
                    this.loading = false;
                }).catch(error => {
                    this.loading = false;
                    this.message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
                });
            }
        },
        closeAddMemberModel() {
            $('#addMemberModel').modal('hide');
        },
        async loadUserDistrictAndPanchayaths() {
            this.selectedDistrict = this.$route.params.id || this.currentUser.district_id;
            // this.selectedDistrict = 13;
            await this.fetchZones(this.selectedDistrict);
            await this.fetchPanchayaths(this.selectedDistrict);
        },
        async fetchPanchayaths(districtId) {
            try {
                const response = await fetch(`https://guide.msoftit.com/api/location/panchayaths?district_id=${districtId}&zone_id=${this.selectedZone}`);
                const data = await response.json();
                this.panchayaths = data.data.panchayaths;
            } catch (e) {
                this.panchayaths = [];
            }
        },
        async fetchWards(panchayathId) {
            try {
                const response = await fetch(`https://guide.msoftit.com/api/location/wards?panchayath_id=${panchayathId}`);
                const data = await response.json();
                this.wards = data.data.wards;
            } catch (e) {
                this.wards = [];
            }
        },
        async onPanchayathChange() {
            this.selectedWard = null;
            if (this.selectedPanchayath) {
                await this.fetchWards(this.selectedPanchayath);
            } else {
                this.wards = [];
            }
        },
        async onZoneChange() {
            this.selectedPanchayath = null;
            this.selectedWard = null;
            this.wards = [];
            if (this.selectedZone) {
                await this.fetchPanchayaths(this.selectedDistrict);
            } else {
                this.panchayaths = [];
            }
        },
        async fetchZones(districtId) {
            let data = {
                district_id: districtId
            };
            try {
                const response = await UserService.authPostRequest('get-zone-by-id', data);
                if (response.data.status === 'success') {
                    this.zones = response.data.data.list;
                } else {
                    this.zones = [];
                }
            } catch (e) {
                this.zones = [];
            }
        },
        panchayathView(group) {
            $('#addMemberModel').modal('show');
            this.selectedZone = group.clusters[0]?.zone?.id || '';
            this.selectedPanchayath = group.clusters[0]?.ward?.panchayath_id || '';
            this.onPanchayathChange().then(() => {
                const wardIds = group.clusters.map(c => c.ward_id).filter(id => id);
                this.selectedWards = this.wards.filter(w => wardIds.includes(w.id));
            });
            this.editable_id = null;
            this.editing_panchayath = true;
        },
        panchayathDelete(group) {
            const clusterIds = group.clusters.map(c => c.id);
            this.$swal.fire({
                title: '',
                text: `Are you sure you want to delete all clusters in ${group.name}?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete them!'
            }).then((result) => {
                if (result.isConfirmed) {
                    // Delete all clusters in this Panchayat
                    const deletePromises = clusterIds.map(id => 
                        UserService.authPostRequest('delete-district-clusters', { id })
                    );
                    Promise.all(deletePromises).then(() => {
                        this.$swal.fire('', 'Clusters deleted successfully', 'success');
                        this.getMembersList(1);
                    }).catch(() => {
                        this.$swal.fire('', 'Error deleting clusters', 'error');
                    });
                }
            });
        },
        startDeleteMode(group) {
            this.deleteMode = true;
            this.deleteModeGroup = group.name;
            this.selectedForDelete = [];
        },
        cancelDeleteMode() {
            this.deleteMode = false;
            this.deleteModeGroup = null;
            this.selectedForDelete = [];
        },
        deleteSelectedClusters() {
            if (this.selectedForDelete.length === 0) {
                this.$swal.fire('', 'Please select clusters to delete', 'warning');
                return;
            }
            
            this.$swal.fire({
                title: '',
                text: `Are you sure you want to delete ${this.selectedForDelete.length} selected cluster(s)?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete them!'
            }).then((result) => {
                if (result.isConfirmed) {
                    // Delete only selected clusters
                    const deletePromises = this.selectedForDelete.map(id => 
                        UserService.authPostRequest('delete-district-clusters', { id })
                    );
                    Promise.all(deletePromises).then(() => {
                        this.$swal.fire('', 'Selected clusters deleted successfully', 'success');
                        this.getMembersList(1);
                        this.cancelDeleteMode();
                    }).catch(() => {
                        this.$swal.fire('', 'Error deleting clusters', 'error');
                    });
                }
            });
        },
         toggleSelectAll() {
            if (this.loading) return;
            
            if (this.allWardsSelected) {
                this.selectedWards = [];
            } else {
                this.selectedWards = [...this.wards];
            }
        },
        isWardSelected(ward) {
            return this.selectedWards.some(selectedWard => selectedWard.id === ward.id);
        },
        toggleWardSelection(ward) {
            if (this.loading) return;
            
            const index = this.selectedWards.findIndex(selectedWard => selectedWard.id === ward.id);
            
            if (index > -1) {
                this.selectedWards.splice(index, 1);
            } else {
                this.selectedWards.push(ward);
            }
        },
    },
};
</script>
<style scoped>
@import '~vue-multiselect/dist/vue-multiselect.min.css';

.section-heading {
    padding: 10px 10px 0px 10px;
}

.error-feedback {
    color: red;
}

.listview {
    /* margin-bottom: 20px; */
}

.edit-btn {
    margin-left: 8px;
}

.progress {
    width: 80px;
}

.listview>li footer {
    color: #171617;
}

.min-width-50 {
    min-width: 60% !important;
}

.action-button-list>li {
    min-height: 1px !important;
}

.sl-no {
    font-weight: 900;
    margin-right: 12px;
}

.deleted-list-title {
    font-size: 15px;
}

.deleted-list {
    margin-bottom: 20px;
}

.deleted-info {
    background-color: #f2bbca;
    font-size: 12px;
}

.deleted-info strong {
    font-size: 18px;
    font-weight: 600;
}

.image-listview.text>li:after {
    left: 0px;
    /* height: 1px; */
    background-color: #1a418c;
}

.verification-badge {
    margin-left: 5px;
}

.avatar-section .button {
    color: #fff;
    width: 18px;
    height: 18px;
    border-radius: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    position: absolute;
    right: 10px;
    top: 24px;
    bottom: 0;
}

.avatar-section .verified {
    background: #4dc81c;
}

.avatar-section .not-verified {
    background: #eb3941;
}

.green-tick {
    font-size: 18px;
    color: darkgreen;
}

.red-cross {
    font-size: 18px;
    color: red;
}

.qhls-text {
    font-style: italic;
    color: #266bd1;
}

.stat-box {
    background: #ffffff;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.09);
    border-radius: 10px;
    padding: 10px 10px;
}

.card {
    border-radius: 0.75rem;
}

.card .btn {
    min-width: 32px;
}

.clustors-main {
    margin: 15px auto;
    padding: 0px 18px;
}

.clustors-header {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
}

.clustors-search {
    flex: 1;
}

.clustors-add-btn {
    white-space: nowrap;
}

.clustors-list {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.clustors-card {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 10px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 1px 2px rgba(0,0,0,0.02);
}

.clustors-name {
    color: #000000;
    font-size: 1.05rem;
}

.clustors-actions button {
    margin-left: 8px;
}

.mobile-multiselect {
    min-width: 100%;
}

.clustors-panchayat {
  display: block;
  font-size: 0.9em;
  color: #888;
  margin-top: 2px;
}

.panchayath-heading {
  font-weight: bold;
  font-size: 1.1em;
  margin-bottom: 5px;
  color: #266bd1;
  padding-left: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
}

.panchayath-actions {
  display: flex;
  gap: 5px;
}

.cluster-content {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cluster-checkbox {
  margin: 0;
}

.delete-actions {
  display: flex;
  gap: 5px;
}
.delete-mode-actions {
  display: flex;
  gap: 5px;
}

.zone-group {
    margin-bottom: 5px;
    background: #f8f9fa;
    border-radius: 12px;
    padding: 15px;
}

.zone-heading {
    font-weight: bold;
    font-size: 1.2em;
    margin-bottom: 15px;
    color: #1a418c;
    padding: 5px 10px;
    background: #fff;
    border-radius: 8px;
    text-align: left;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.mobile-multiselect .multiselect__content-wrapper {
    max-height: 200px !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch !important;
    overscroll-behavior: contain;
    border-radius: 0 0 8px 8px;
}

.mobile-multiselect .multiselect__option {
    min-height: 44px !important;
    padding: 12px 16px !important;
    display: flex !important;
    align-items: center !important;
    line-height: 1.4 !important;
    font-size: 16px !important;
    border-bottom: 1px solid #f0f0f0;
}

.mobile-multiselect .multiselect__option:last-child {
    border-bottom: none;
}

.mobile-multiselect .multiselect__option--selected {
    background-color: #e3f2fd !important;
    color: #1976d2 !important;
    font-weight: 500 !important;
}

.mobile-multiselect .multiselect__option--selected::after {
    content: '✓';
    color: #1976d2;
    font-weight: bold;
    margin-left: auto;
}

.mobile-multiselect .multiselect__option--highlight {
    background-color: #f5f5f5 !important;
    color: #333 !important;
}

.mobile-multiselect .multiselect__input {
    font-size: 16px !important;
    padding: 8px 12px !important;
    min-height: 20px !important;
}

.mobile-multiselect .multiselect__placeholder {
    font-size: 16px !important;
    color: #999 !important;
    padding: 12px 16px !important;
    margin-bottom: 0 !important;
}

.mobile-multiselect .multiselect__tags {
    min-height: 48px !important;
    padding: 8px 40px 8px 12px !important;
    border: 2px solid #ddd !important;
    border-radius: 8px !important;
    font-size: 16px !important;
}

.mobile-multiselect .multiselect__tag {
    background-color: #2196f3 !important;
    color: white !important;
    border-radius: 16px !important;
    padding: 6px 12px !important;
    margin: 2px 4px 2px 0 !important;
    font-size: 14px !important;
    display: inline-flex !important;
    align-items: center !important;
    max-width: calc(100% - 30px) !important;
}

.mobile-multiselect .multiselect__tag-icon {
    width: 18px !important;
    height: 18px !important;
    border-radius: 50% !important;
    background-color: rgba(255, 255, 255, 0.3) !important;
    margin-left: 8px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-size: 12px !important;
    line-height: 1 !important;
}

.mobile-multiselect .multiselect__tag-icon::after {
    content: '×' !important;
    color: white !important;
    font-weight: bold !important;
}

.mobile-multiselect .multiselect__select {
    width: 40px !important;
    height: 100% !important;
    right: 0 !important;
    top: 0 !important;
    padding: 4px 8px !important;
    text-align: center !important;
}

.mobile-multiselect .multiselect__select::before {
    border-color: #999 transparent transparent !important;
    border-width: 6px 6px 0 !important;
    margin-top: -3px !important;
}

.mobile-multiselect .multiselect__spinner {
    right: 12px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
}

.mobile-multiselect .multiselect--active .multiselect__tags {
    border-color: #2196f3 !important;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2) !important;
}

@media (max-width: 480px) {
    .mobile-multiselect .multiselect__content-wrapper {
        max-height: 160px !important;
    }
    
    .mobile-multiselect .multiselect__option {
        padding: 10px 12px !important;
        font-size: 15px !important;
    }
    
    .mobile-multiselect .multiselect__tags {
        min-height: 44px !important;
        padding: 6px 36px 6px 10px !important;
    }
    
    .mobile-multiselect .multiselect__tag {
        font-size: 13px !important;
        padding: 4px 8px !important;
    }
}

.mobile-multiselect .multiselect__content-wrapper::-webkit-scrollbar {
    width: 6px;
}

.mobile-multiselect .multiselect__content-wrapper::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.mobile-multiselect .multiselect__content-wrapper::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.mobile-multiselect .multiselect__content-wrapper::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

.select-all-container {
    margin-bottom: 0.75rem;
    padding: 0.5rem;
    background-color: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #e9ecef;
}

.select-all-label {
    display: flex;
    align-items: center;
    margin: 0;
    font-weight: 500;
    color: #495057;
    cursor: pointer;
}

.select-all-checkbox {
    margin-right: 0.5rem;
    transform: scale(1.1);
}

.clusters-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
    max-height: 300px;
    overflow-y: auto;
    padding: 0.5rem;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    background-color: #fff;
}

.cluster-item {
    display: flex;
    align-items: center;
    padding: 0.5rem;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    background-color: #fafafa;
    transition: all 0.2s ease;
    cursor: pointer;
}

.cluster-item:hover {
    background-color: #e3f2fd;
    border-color: #90caf9;
}

.cluster-item.selected {
    background-color: #e8f5e8;
    border-color: #357737;
}

.cluster-checkbox {
    margin-right: 0.5rem;
    flex-shrink: 0;
}

.cluster-name {
    font-size: 0.9rem;
    line-height: 1.2;
    color: #333;
    word-break: break-word;
}

.selected-count {
    margin-top: 0.5rem;
    text-align: center;
    padding: 0.25rem;
    background-color: #e3f2fd;
    border-radius: 4px;
}

.text-muted {
    color: #6c757d;
}

.text-danger {
    color: #dc3545;
}

.small {
    font-size: 0.875rem;
}

/* Mobile specific adjustments */
@media (max-width: 576px) {
    .clusters-grid {
        grid-template-columns: 1fr;
        gap: 0.25rem;
    }
    
    .cluster-item {
        padding: 0.75rem 0.5rem;
    }
    
    .cluster-name {
        font-size: 0.95rem;
    }
}

/* Very small screens */
@media (max-width: 400px) {
    .cluster-item {
        padding: 0.5rem 0.25rem;
    }
    
    .cluster-name {
        font-size: 0.85rem;
    }
}

/* Custom scrollbar for better mobile experience */
.clusters-grid::-webkit-scrollbar {
    width: 4px;
}

.clusters-grid::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
}

.clusters-grid::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
}

.clusters-grid::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}
</style>